import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { Message } from 'primereact/message';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import SearchBarAtom from '../../atoms/SearchBar/SearchBarAtom';
import SpinnerAtom from '../../atoms/Spinner/SpinnerAtom';
import { StatusBadgeAtom, StatusBadgeStatus } from '../../atoms/StatusBadge';
import ConfirmDialogAtom, {
  confirmDialogAtom,
} from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import CustomDialog from '../../atoms/CustomDialog/CustomDialogAtom';
import DataGridAtom, {
  DataGridAction,
  DataGridColumn,
} from '../../atoms/DataGrid/DataGridAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import DomainJoinCheckbox from '../../atoms/checkbox/Checkbox';
import './WaitListManagerOrganism.scss';

export type WaitlistStatus = 'pending' | 'approved' | 'rejected';
export type WaitlistStatusFilter = WaitlistStatus | 'all';
export type WaitlistSignupSource = 'email' | 'google' | 'microsoft' | 'linkedin';

export interface WaitlistUser {
  id: string;
  email: string;
  displayName: string | null;
  organizationId: string | null;
  organizationName: string | null;
  organizationDomain: string | null;
  centerId: string | null;
  centerName: string | null;
  status: WaitlistStatus;
  signupSource: WaitlistSignupSource;
  createdAt: string;
  approvedBy: string | null;
  /** How the row was approved, as the server reports it; `null` while not approved. */
  approvalSource: 'domain_auto_join' | 'admin' | null;
  approvedAt: string | null;
  notes: string | null;
}

export interface WaitlistOrganizationOption {
  id: string;
  name: string;
  domain: string | null;
  centerId: string | null;
  centerName: string | null;
  /** True when the organization already admits verified signups from its domain. */
  domainJoinApproved?: boolean;
}

export interface WaitListManagerOrganismProps {
  /** Called when the organization-service returns 401/403. Consumers may redirect to login. */
  onUnauthorized?: () => void;
  /** Initial status filter (defaults to 'pending'). */
  initialStatusFilter?: WaitlistStatusFilter;
}

interface ApiError {
  response?: { status?: number };
}

const isUnauthorizedError = (err: unknown): boolean => {
  const status = (err as ApiError)?.response?.status;
  return status === 401 || status === 403;
};

/**
 * Map waitlist status to the closest StatusBadgeAtom status.
 * pending → pending, approved → active, rejected → error
 */
const statusToBadge = (status: WaitlistStatus): StatusBadgeStatus => {
  if (status === 'approved') return 'active';
  if (status === 'rejected') return 'error';
  return 'pending';
};

/**
 * PrimeReact's selection checkbox gives its root `<div>` `role="checkbox"` and the name while its
 * real `<input>` carries both too (axe `nested-interactive`). The input is the control, so the root
 * is styling only.
 */
const SELECTION_CHECKBOX_PT = {
  root: { role: undefined, 'aria-checked': undefined, 'aria-label': undefined },
};

const emailDomain = (email: string): string =>
  email.slice(email.lastIndexOf('@') + 1).trim().toLowerCase();

/** The consequence line applies only when approving opens the org's own, not-yet-approved domain. */
const opensDomainJoin = (
  email: string,
  org: { domain: string | null; domainJoinApproved?: boolean } | null | undefined,
): boolean =>
  !!org?.domain &&
  !org.domainJoinApproved &&
  emailDomain(email) === org.domain.trim().toLowerCase();

interface DomainJoinConsequenceProps {
  inputId: string;
  domain: string;
  orgName: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const DomainJoinConsequence: React.FC<DomainJoinConsequenceProps> = ({
  inputId,
  domain,
  orgName,
  checked,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <DomainJoinCheckbox
      className="waitlist-manager__domain-join"
      inputId={inputId}
      checked={checked}
      onChange={onChange}
      label={t('auth.admin.domainJoin.consequence', { domain, organization: orgName })}
    />
  );
};

/** Owns the checkbox state for the imperative confirm dialog, which cannot hold React state itself. */
const ConfirmDomainJoin: React.FC<
  Omit<DomainJoinConsequenceProps, 'checked' | 'onChange'> & {
    message: string;
    onChange: (checked: boolean) => void;
  }
> = ({ message, onChange, ...rest }) => {
  const [checked, setChecked] = useState(true);
  return (
    <div className="waitlist-manager__confirm">
      <p>{message}</p>
      <DomainJoinConsequence
        {...rest}
        checked={checked}
        onChange={(next) => {
          setChecked(next);
          onChange(next);
        }}
      />
    </div>
  );
};

/** One organization's domain that a bulk approval would open, and the selected emails it covers. */
interface BulkDomainJoinGroup {
  orgId: string;
  domain: string;
  orgName: string;
  emails: string[];
}

/** The server's bulk route caps each request at this many emails. */
const BULK_APPROVE_CHUNK_SIZE = 50;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

/** Bulk confirm body: states each domain the selection would open, one opt-out checkbox per domain. */
const ConfirmBulkDomainJoin: React.FC<{
  message: string;
  groups: BulkDomainJoinGroup[];
  onChange: (orgId: string, checked: boolean) => void;
}> = ({ message, groups, onChange }) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groups.map((g) => [g.orgId, true])),
  );
  return (
    <div className="waitlist-manager__confirm">
      <p>{message}</p>
      {groups.map((group) => (
        <DomainJoinCheckbox
          key={group.orgId}
          className="waitlist-manager__domain-join"
          inputId={`waitlist-manager-bulk-domain-join-${group.orgId}`}
          checked={checked[group.orgId] ?? true}
          onChange={(next) => {
            setChecked((prev) => ({ ...prev, [group.orgId]: next }));
            onChange(group.orgId, next);
          }}
          label={t('auth.admin.domainJoin.bulkConsequence', {
            domain: group.domain,
            organization: group.orgName,
          })}
        />
      ))}
    </div>
  );
};

const WaitListManagerOrganism: React.FC<WaitListManagerOrganismProps> = ({
  onUnauthorized,
  initialStatusFilter = 'pending',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [users, setUsers] = useState<WaitlistUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<WaitlistUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The outcome of a bulk approve that did not fully succeed. Unlike `error` it leaves the grid in place, so the people who were not approved stay selected and retryable.
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] =
    useState<WaitlistStatusFilter>(initialStatusFilter);
  const [searchValue, setSearchValue] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);

  const [orgDialogVisible, setOrgDialogVisible] = useState(false);
  const [organizations, setOrganizations] = useState<WaitlistOrganizationOption[]>([]);
  const [selectedOrg, setSelectedOrg] =
    useState<WaitlistOrganizationOption | null>(null);
  const [currentUserForOrgSelection, setCurrentUserForOrgSelection] =
    useState<WaitlistUser | null>(null);
  const [orgDialogDomainJoin, setOrgDialogDomainJoin] = useState(true);

  const statusOptions = useMemo(
    () => [
      { label: t('auth.admin.filters.statusAll'), value: 'all' },
      { label: t('auth.admin.filters.statusPending'), value: 'pending' },
      { label: t('auth.admin.filters.statusApproved'), value: 'approved' },
      { label: t('auth.admin.filters.statusRejected'), value: 'rejected' },
    ],
    [t],
  );

  const fetchUsers = useCallback(async () => {
    if (!apiClients?.organizationClient) return;
    setLoading(true);
    setError(null);

    try {
      const response = await apiClients.organizationClient.get('/admin/waitlist', {
        params: {
          status: statusFilter === 'all' ? undefined : statusFilter,
          limit: rows,
          offset: first,
        },
      });
      setUsers(response.users);
      setTotalRecords(response.total);
    } catch (err: unknown) {
      if (isUnauthorizedError(err)) {
        setError(t('auth.admin.errors.forbidden'));
        onUnauthorized?.();
      } else {
        setError(t('auth.admin.errors.fetchFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [apiClients, statusFilter, first, rows, t, onUnauthorized]);

  const fetchOrganizations = useCallback(async (): Promise<WaitlistOrganizationOption[]> => {
    if (!apiClients?.organizationClient) return [];
    try {
      const response = await apiClients.organizationClient.get('/admin/organizations');
      const list: WaitlistOrganizationOption[] = response?.organizations ?? [];
      setOrganizations(list);
      return list;
    } catch (err) {
      console.warn(
        '[WaitListManagerOrganism] Failed to load organizations for approval dialog',
        err,
      );
      return [];
    }
  }, [apiClients]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (orgDialogVisible && organizations.length === 0) {
      fetchOrganizations();
    }
  }, [orgDialogVisible, organizations.length, fetchOrganizations]);

  const filteredUsers = useMemo(() => {
    if (!searchValue) return users;
    const lower = searchValue.toLowerCase();
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(lower) ||
        user.displayName?.toLowerCase().includes(lower) ||
        user.organizationName?.toLowerCase().includes(lower),
    );
  }, [users, searchValue]);

  const approveUserRequest = useCallback(
    async (payload: {
      email: string;
      targetOrganizationId?: string;
      centerId?: string;
      approveDomainJoin?: boolean;
    }) => {
      if (!apiClients?.organizationClient) return;
      await apiClients.organizationClient.post('/admin/waitlist/approve', payload);
    },
    [apiClients],
  );

  const rejectUserRequest = useCallback(
    async (payload: { email: string }) => {
      if (!apiClients?.organizationClient) return;
      await apiClients.organizationClient.post('/admin/waitlist/reject', payload);
    },
    [apiClients],
  );

  const bulkApproveRequest = useCallback(
    // Resolves to the emails that were not approved: the ones the server reported as failed and
    // every email of a chunk whose request threw. The chunks are independent, so one failed
    // request does not stop the rest.
    async (payload: { emails: string[]; approveDomainJoin: boolean }): Promise<string[]> => {
      if (!apiClients?.organizationClient) return payload.emails;
      const notApproved: string[] = [];
      for (const emails of chunk(payload.emails, BULK_APPROVE_CHUNK_SIZE)) {
        try {
          const response = await apiClients.organizationClient.post('/admin/waitlist/bulk-approve', {
            emails,
            approveDomainJoin: payload.approveDomainJoin,
          });
          const results: Array<{ email: string; success: boolean }> = response?.results ?? [];
          notApproved.push(...results.filter((result) => !result.success).map((result) => result.email));
        } catch {
          notApproved.push(...emails);
        }
      }
      return notApproved;
    },
    [apiClients],
  );

  const handleApprove = useCallback(
    async (user: WaitlistUser) => {
      if (!user.organizationId) {
        setCurrentUserForOrgSelection(user);
        setOrgDialogDomainJoin(true);
        setOrgDialogVisible(true);
        return;
      }

      const knownOrgs = organizations.length > 0 ? organizations : await fetchOrganizations();
      const org = knownOrgs.find((o) => o.id === user.organizationId);
      const showDomainJoin = opensDomainJoin(user.email, org);
      let approveDomainJoin = true;
      const confirmMessage = t('auth.admin.confirmApprove', { email: user.email });

      confirmDialogAtom({
        message:
          showDomainJoin && org?.domain ? (
            <ConfirmDomainJoin
              inputId="waitlist-manager-confirm-domain-join"
              message={confirmMessage}
              domain={org.domain}
              orgName={org.name}
              onChange={(next) => {
                approveDomainJoin = next;
              }}
            />
          ) : (
            confirmMessage
          ),
        header: t('auth.admin.confirmApproveHeader'),
        icon: 'pi pi-check-circle',
        acceptLabel: t('auth.admin.approve'),
        rejectLabel: t('auth.admin.cancel'),
        accept: async () => {
          try {
            await approveUserRequest({
              email: user.email,
              targetOrganizationId: user.organizationId || undefined,
              centerId: user.centerId || undefined,
              approveDomainJoin: showDomainJoin ? approveDomainJoin : false,
            });
            await fetchUsers();
            setSelectedUsers([]);
          } catch {
            setError(t('auth.admin.errors.approveFailed'));
          }
        },
      });
    },
    [t, organizations, fetchOrganizations, approveUserRequest, fetchUsers],
  );

  const orgDialogShowsDomainJoin =
    !!currentUserForOrgSelection && opensDomainJoin(currentUserForOrgSelection.email, selectedOrg);

  const handleApproveWithOrg = useCallback(async () => {
    if (!currentUserForOrgSelection || !selectedOrg) return;
    setOrgDialogVisible(false);

    try {
      await approveUserRequest({
        email: currentUserForOrgSelection.email,
        targetOrganizationId: selectedOrg.id,
        centerId: selectedOrg.centerId || undefined,
        approveDomainJoin: orgDialogShowsDomainJoin ? orgDialogDomainJoin : false,
      });
      await fetchUsers();
      setSelectedUsers([]);
      setCurrentUserForOrgSelection(null);
      setSelectedOrg(null);
    } catch {
      setError(t('auth.admin.errors.approveFailed'));
    }
  }, [
    currentUserForOrgSelection,
    selectedOrg,
    orgDialogShowsDomainJoin,
    orgDialogDomainJoin,
    t,
    approveUserRequest,
    fetchUsers,
  ]);

  const handleReject = useCallback(
    (user: WaitlistUser) => {
      confirmDialogAtom({
        message: t('auth.admin.confirmReject', { email: user.email }),
        header: t('auth.admin.confirmRejectHeader'),
        icon: 'pi pi-times-circle',
        acceptLabel: t('auth.admin.reject'),
        rejectLabel: t('auth.admin.cancel'),
        variant: 'danger',
        accept: async () => {
          try {
            await rejectUserRequest({ email: user.email });
            await fetchUsers();
            setSelectedUsers([]);
          } catch {
            setError(t('auth.admin.errors.rejectFailed'));
          }
        },
      });
    },
    [t, rejectUserRequest, fetchUsers],
  );

  const handleBulkApprove = useCallback(async () => {
    if (selectedUsers.length === 0) return;
    const usersWithoutOrg = selectedUsers.filter((u) => !u.organizationId);
    if (usersWithoutOrg.length > 0) {
      setError(t('auth.admin.errors.bulkApproveNeedsOrg'));
      return;
    }

    const knownOrgs = organizations.length > 0 ? organizations : await fetchOrganizations();
    const orgById = new Map(knownOrgs.map((o) => [o.id, o]));
    const groups = new Map<string, BulkDomainJoinGroup>();
    for (const user of selectedUsers) {
      const org = orgById.get(user.organizationId as string);
      if (!org?.domain || !opensDomainJoin(user.email, org)) continue;
      const group = groups.get(org.id) ?? {
        orgId: org.id,
        domain: org.domain,
        orgName: org.name,
        emails: [],
      };
      group.emails.push(user.email);
      groups.set(org.id, group);
    }
    const domainGroups = [...groups.values()];
    const optedIn = new Set(domainGroups.map((g) => g.orgId));
    const confirmMessage = t('auth.admin.confirmBulkApprove', { count: selectedUsers.length });

    confirmDialogAtom({
      message:
        domainGroups.length > 0 ? (
          <ConfirmBulkDomainJoin
            message={confirmMessage}
            groups={domainGroups}
            onChange={(orgId, next) => {
              if (next) optedIn.add(orgId);
              else optedIn.delete(orgId);
            }}
          />
        ) : (
          confirmMessage
        ),
      header: t('auth.admin.confirmBulkApproveHeader'),
      icon: 'pi pi-check-circle',
      acceptLabel: t('auth.admin.bulkApprove', { count: selectedUsers.length }),
      rejectLabel: t('auth.admin.cancel'),
      accept: async () => {
        // Only a domain the admin saw and left checked is opened; every other entry sends false.
        const joinEmails = new Set(
          domainGroups.filter((g) => optedIn.has(g.orgId)).flatMap((g) => g.emails),
        );
        const allEmails = selectedUsers.map((u) => u.email);
        const withJoin = allEmails.filter((email) => joinEmails.has(email));
        const withoutJoin = allEmails.filter((email) => !joinEmails.has(email));
        setBulkNotice(null);
        const notApproved = new Set<string>();
        if (withJoin.length > 0) {
          (await bulkApproveRequest({ emails: withJoin, approveDomainJoin: true })).forEach((email) =>
            notApproved.add(email),
          );
        }
        if (withoutJoin.length > 0) {
          (await bulkApproveRequest({ emails: withoutJoin, approveDomainJoin: false })).forEach((email) =>
            notApproved.add(email),
          );
        }
        // The list always reflects what the server approved, and only the people it did not
        // approve stay selected, so a retry never re-sends the ones that went through.
        await fetchUsers();
        setSelectedUsers((current) => current.filter((user) => notApproved.has(user.email)));
        if (notApproved.size === allEmails.length) {
          setBulkNotice(t('auth.admin.errors.bulkApproveFailed'));
        } else if (notApproved.size > 0) {
          setBulkNotice(t('auth.admin.errors.bulkApprovePartial', { count: notApproved.size }));
        }
      },
    });
  }, [selectedUsers, organizations, fetchOrganizations, t, bulkApproveRequest, fetchUsers]);

  // ----- Cell templates -----

  const emailBody = (rowData: WaitlistUser) => (
    <span className="waitlist-manager__email">{rowData.email}</span>
  );

  const displayNameBody = (rowData: WaitlistUser) => (
    <span className={rowData.displayName ? undefined : 'waitlist-manager__placeholder'}>
      {rowData.displayName || '—'}
    </span>
  );

  const organizationBody = (rowData: WaitlistUser) => {
    if (rowData.organizationName) {
      return (
        <span className="waitlist-manager__org-detected">
          <i className="pi pi-check-circle" /> {rowData.organizationName}
        </span>
      );
    }
    if (rowData.organizationDomain) {
      return (
        <span className="waitlist-manager__org-not-found">
          <i className="pi pi-exclamation-triangle" />{' '}
          {t('auth.admin.noOrgFound', { domain: rowData.organizationDomain })}
        </span>
      );
    }
    return <span className="waitlist-manager__placeholder">—</span>;
  };

  const SOURCE_ICONS: Record<string, string> = {
    google: 'pi pi-google',
    microsoft: 'pi pi-microsoft',
    linkedin: 'pi pi-linkedin',
    email: 'pi pi-envelope',
  };

  const signupSourceBody = (rowData: WaitlistUser) => {
    const icon = SOURCE_ICONS[rowData.signupSource] ?? 'pi pi-user';
    return (
      <span className="waitlist-manager__source">
        <i className={icon} />
        {rowData.signupSource}
      </span>
    );
  };

  const statusBody = (rowData: WaitlistUser) => (
    <span className="waitlist-manager__status">
      <StatusBadgeAtom
        status={statusToBadge(rowData.status)}
        label={t(`auth.admin.status.${rowData.status}`)}
        size="small"
        showIcon
      />
      {rowData.approvalSource === 'domain_auto_join' && (
        <span className="waitlist-manager__approved-by">
          {t('auth.admin.source.domainAutoJoin')}
        </span>
      )}
    </span>
  );

  const createdAtBody = (rowData: WaitlistUser) => {
    const d = rowData.createdAt ? new Date(rowData.createdAt) : null;
    const valid = d && !Number.isNaN(d.getTime());
    return (
      <span className={valid ? undefined : 'waitlist-manager__placeholder'}>
        {valid ? d.toLocaleDateString() : '—'}
      </span>
    );
  };

  const columns: DataGridColumn[] = useMemo(
    () => [
      // Without a selection column PrimeReact's `checkbox` mode keeps one row selected, so bulk approve could never see more.
      {
        field: 'select',
        header: '',
        selectionMode: 'multiple',
        pt: { headerCheckbox: SELECTION_CHECKBOX_PT, rowCheckbox: SELECTION_CHECKBOX_PT },
        width: '3rem',
        minWidth: '3rem',
      },
      {
        field: 'email',
        header: t('auth.admin.columns.email'),
        body: emailBody,
        sortable: true,
      },
      {
        field: 'displayName',
        header: t('auth.admin.columns.displayName'),
        body: displayNameBody,
      },
      {
        field: 'organizationName',
        header: t('auth.admin.columns.organization'),
        body: organizationBody,
      },
      {
        field: 'signupSource',
        header: t('auth.admin.columns.signupSource'),
        body: signupSourceBody,
      },
      {
        field: 'status',
        header: t('auth.admin.columns.status'),
        body: statusBody,
        sortable: true,
      },
      {
        field: 'createdAt',
        header: t('auth.admin.columns.createdAt'),
        body: createdAtBody,
        sortable: true,
      },
    ],
    [t],
  );

  const rowActions: DataGridAction[] = useMemo(
    () => [
      {
        id: 'approve',
        label: t('auth.admin.approve'),
        icon: 'pi pi-check',
        severity: 'success',
        onClick: handleApprove,
        visible: (row) => (row as WaitlistUser).status === 'pending',
      },
      {
        id: 'reject',
        label: t('auth.admin.reject'),
        icon: 'pi pi-times',
        severity: 'danger',
        onClick: handleReject,
        visible: (row) => (row as WaitlistUser).status === 'pending',
      },
    ],
    [t, handleApprove, handleReject],
  );

  // ----- Render -----

  if (loading) {
    return (
      <div className="waitlist-manager">
        <div className="waitlist-manager__loading">
          <SpinnerAtom variant="primary" />
          <p>{t('auth.admin.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="waitlist-manager">
        <Message severity="error" text={error} />
      </div>
    );
  }

  return (
    <div className="waitlist-manager">
      <ConfirmDialogAtom />
      <CustomDialog
        header={t('auth.admin.selectOrganization')}
        visible={orgDialogVisible}
        onHide={() => {
          setOrgDialogVisible(false);
          setCurrentUserForOrgSelection(null);
          setSelectedOrg(null);
        }}
        style={{ width: '480px' }}
        footer={
          <div className="waitlist-manager__org-dialog-footer">
            <ButtonAtom
              label={t('auth.admin.cancel')}
              icon="pi pi-times"
              variant="text"
              onClick={() => {
                setOrgDialogVisible(false);
                setCurrentUserForOrgSelection(null);
                setSelectedOrg(null);
              }}
            />
            <ButtonAtom
              label={t('auth.admin.approve')}
              icon="pi pi-check"
              variant="primary"
              onClick={handleApproveWithOrg}
              disabled={!selectedOrg}
              autoFocus
            />
          </div>
        }
      >
        <div className="waitlist-manager__org-dialog">
          <p>
            {t('auth.admin.selectOrganizationMessage', {
              email: currentUserForOrgSelection?.email || '',
            })}
          </p>
          <DropdownAtom
            value={selectedOrg}
            options={organizations}
            onChange={(e) => setSelectedOrg(e.value)}
            optionLabel="name"
            placeholder={t('auth.admin.selectOrganizationPlaceholder')}
            filter
            className="waitlist-manager__org-dropdown"
          />
          {orgDialogShowsDomainJoin && selectedOrg?.domain && (
            <DomainJoinConsequence
              inputId="waitlist-manager-org-dialog-domain-join"
              domain={selectedOrg.domain}
              orgName={selectedOrg.name}
              checked={orgDialogDomainJoin}
              onChange={setOrgDialogDomainJoin}
            />
          )}
        </div>
      </CustomDialog>

      <div className="waitlist-manager__card">
        <header className="waitlist-manager__card-header">
          <h1 className="waitlist-manager__title">{t('auth.admin.title')}</h1>
          <div className="waitlist-manager__card-header-actions">
            {selectedUsers.length > 0 && (
              <ButtonAtom
                icon="pi pi-check"
                label={t('auth.admin.bulkApprove', { count: selectedUsers.length })}
                variant="primary"
                onClick={handleBulkApprove}
              />
            )}
          </div>
        </header>

        {bulkNotice && <Message severity="error" text={bulkNotice} />}

        <div className="waitlist-manager__filters">
          <div className="waitlist-manager__filter-field">
            <label htmlFor="waitlist-manager-status-filter">
              {t('auth.admin.filters.status')}
            </label>
            <DropdownAtom
              inputId="waitlist-manager-status-filter"
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.value)}
              placeholder={t('auth.admin.filters.statusAll')}
            />
          </div>
          <div className="waitlist-manager__filter-field waitlist-manager__filter-field--grow">
            <label htmlFor="waitlist-manager-search-input">
              {t('auth.admin.filters.search')}
            </label>
            <SearchBarAtom
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t('auth.admin.filters.searchPlaceholder')}
            />
          </div>
        </div>

        <DataGridAtom
          data={filteredUsers}
          columns={columns}
          actions={rowActions}
          showActions
          selectionMode="checkbox"
          selection={selectedUsers}
          onSelectionChange={(e) => setSelectedUsers(e.value as WaitlistUser[])}
          paginator
          rows={rows}
          rowsPerPageOptions={[25, 50, 100]}
          first={first}
          onPage={(event: { first: number; rows: number }) => {
            setFirst(event.first);
            setRows(event.rows);
          }}
          totalRecords={totalRecords}
          lazy
          emptyMessage={t('auth.admin.noUsers')}
          showHeader={false}
          dataKey="id"
          className="waitlist-manager__table"
        />
      </div>
    </div>
  );
};

export default WaitListManagerOrganism;
